import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import { GetStartedCTA } from "../components/DemoDataControls";
import {
  getPriceLists, getPriceListWithItems, createPriceList, updatePriceList,
  archivePriceList, setDefaultPriceList, addItem, updateItem, removeItem,
} from "../lib/priceLists";
import { getProducts } from "../lib/products";      // read-only
import { listUnits } from "../lib/productUnits";     // read-only
import ProductPicker from "../products/ProductPicker";

const CURRENCIES = ["EUR", "GBP", "USD"];
const STATUS_COLOR = { active: C.success, inactive: C.warning, archived: C.textDim };
const money = (n, cur = "EUR") => (n == null ? "—" : `${cur === "EUR" ? "€" : cur + " "}${Number(n).toFixed(2)}`);

const ErrorBar = ({ error, onDismiss }) => error ? (
  <div role="alert" style={{ display: "flex", alignItems: "center", gap: 10, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "8px 12px", margin: "8px 0", fontSize: 13 }}>
    <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
    <button style={S.btn("secondary")} onClick={onDismiss}>Dismiss</button>
  </div>
) : null;

// ── Create / Edit modal ───────────────────────────────────────────────────────
function PriceListForm({ initial, onCancel, onSave }) {
  const seed = initial || {};
  const [f, setF] = useState({
    name: seed.name || "", currency: seed.currency || "EUR",
    status: seed.status || "active", notes: seed.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.name.trim()) { setError("Name is required."); return; }
    setBusy(true); setError("");
    try { await onSave(f); } catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={{ ...S.modalBox, width: 480 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{seed.id ? "Edit Price List" : "New Price List"}</h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>
        <div style={{ marginBottom: 10 }}><label style={S.label}>Name *</label>
          <input style={S.input} value={f.name} onChange={(e) => set("name")(e.target.value)} placeholder="Wholesale — Tier 1" aria-label="Price list name" /></div>
        <div style={S.grid2}>
          <div><label style={S.label}>Currency</label>
            <select style={S.input} value={f.currency} onChange={(e) => set("currency")(e.target.value)} aria-label="Currency">
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select></div>
          <div><label style={S.label}>Status</label>
            <select style={S.input} value={f.status} onChange={(e) => set("status")(e.target.value)} aria-label="Status">
              <option value="active">Active</option><option value="inactive">Inactive</option><option value="archived">Archived</option>
            </select></div>
        </div>
        <div style={{ marginTop: 10 }}><label style={S.label}>Notes</label>
          <textarea style={{ ...S.input, height: 64 }} value={f.notes} onChange={(e) => set("notes")(e.target.value)} /></div>
        <ErrorBar error={error} onDismiss={() => setError("")} />
        <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
          <button style={S.btn("secondary")} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={S.btn()} onClick={submit} disabled={busy}>{busy ? "Saving…" : seed.id ? "Save" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Detail = items editor ──────────────────────────────────────────────────────
function PriceListDetail({ list, products, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [unitsByProduct, setUnitsByProduct] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({ unit_id: "", min_qty: "1", unit_price: "" });
  const [add, setAdd] = useState(null);   // { product, unit_id, min_qty, unit_price }

  const productsById = Object.fromEntries((products || []).map((p) => [p.id, p]));
  const cur = list.currency || "EUR";

  const ensureUnits = useCallback(async (productId) => {
    if (!productId) return [];
    let us = unitsByProduct[productId];
    if (!us) { us = await listUnits(productId).catch(() => []); setUnitsByProduct((m) => ({ ...m, [productId]: us })); }
    return us;
  }, [unitsByProduct]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const pl = await getPriceListWithItems(list.id);
      const rows = pl?.items || [];
      setItems(rows);
      const ids = [...new Set(rows.map((r) => r.product_id))];
      const fetched = await Promise.all(ids.map((id) => listUnits(id).catch(() => [])));
      setUnitsByProduct(Object.fromEntries(ids.map((id, i) => [id, fetched[i]])));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [list.id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => { setBusy(true); setError(""); try { await fn(); await load(); onChanged && onChanged(); } catch (e) { setError(e.message); } finally { setBusy(false); } };

  const unitLabel = (item) => {
    if (!item.unit_id) return "Base";
    const u = (unitsByProduct[item.product_id] || []).find((x) => x.id === item.unit_id);
    return u ? `${u.code}${Number(u.factor) !== 1 ? ` ×${Number(u.factor)}` : ""}` : "unit";
  };

  const startAdd = async (product) => {
    const us = await ensureUnits(product.id);
    const base = us.find((u) => u.is_base) || us[0];
    setAdd({ product, unit_id: base?.id || "", min_qty: "1", unit_price: "" });
  };
  const submitAdd = () => {
    if (add.unit_price === "" || Number(add.unit_price) < 0) { setError("Enter a unit price of 0 or more."); return; }
    act(async () => {
      await addItem(list.id, { product_id: add.product.id, unit_id: add.unit_id || null, min_qty: Number(add.min_qty) || 1, unit_price: Number(add.unit_price) });
      setAdd(null);
    });
  };

  const startEdit = (it) => { setEditingId(it.id); setEdit({ unit_id: it.unit_id || "", min_qty: String(it.min_qty), unit_price: String(it.unit_price) }); };
  const saveEdit = (id) => {
    if (edit.unit_price === "" || Number(edit.unit_price) < 0) { setError("Enter a unit price of 0 or more."); return; }
    act(async () => { await updateItem(id, { unit_id: edit.unit_id || null, min_qty: Number(edit.min_qty) || 1, unit_price: Number(edit.unit_price) }); setEditingId(null); });
  };

  const unitOptions = (productId) => (unitsByProduct[productId] || []);

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modalBox, width: 860 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{list.name}
            {list.is_default && <span style={{ ...S.pill(C.primary), marginLeft: 8 }}>default</span>}
            <span style={{ ...S.pill(STATUS_COLOR[list.status] || C.textDim), marginLeft: 6 }}>{list.status}</span>
            <span style={{ color: C.textMuted, fontWeight: 500, fontSize: 13, marginLeft: 8 }}>{cur}</span>
          </h2>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>
        <div style={{ color: C.textMuted, fontSize: 12.5, marginBottom: 8 }}>Prices are per unit. Add multiple rows for one product at different min quantities to create quantity breaks.</div>
        <ErrorBar error={error} onDismiss={() => setError("")} />

        {loading ? <div style={S.empty}>Loading…</div> : (
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Product</th><th style={S.th}>Unit</th><th style={S.th}>Min qty</th><th style={S.th}>Unit price</th><th style={S.th} aria-label="Actions"></th>
            </tr></thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td style={S.empty} colSpan={5}>No prices yet. Add a product below.</td></tr>
              ) : items.map((it) => {
                const editing = editingId === it.id;
                const prod = productsById[it.product_id];
                return (
                  <tr key={it.id}>
                    <td style={S.td}><strong>{prod?.name || it.product_id}</strong>{prod?.sku && <span style={{ color: C.textMuted, fontSize: 11, marginLeft: 6 }}>{prod.sku}</span>}</td>
                    {editing ? (
                      <>
                        <td style={S.td}>
                          <select style={{ ...S.input, width: 130 }} value={edit.unit_id} onChange={(e) => setEdit((p) => ({ ...p, unit_id: e.target.value }))} aria-label="Unit">
                            <option value="">Base</option>
                            {unitOptions(it.product_id).map((u) => <option key={u.id} value={u.id}>{u.code}</option>)}
                          </select>
                        </td>
                        <td style={S.td}><input style={{ ...S.input, width: 70 }} type="number" min="1" step="1" value={edit.min_qty} onChange={(e) => setEdit((p) => ({ ...p, min_qty: e.target.value }))} aria-label="Min qty" /></td>
                        <td style={S.td}><input style={{ ...S.input, width: 90 }} type="number" min="0" step="0.01" value={edit.unit_price} onChange={(e) => setEdit((p) => ({ ...p, unit_price: e.target.value }))} aria-label="Unit price" /></td>
                        <td style={S.td}>
                          <button style={{ ...S.btn(), padding: "3px 8px" }} onClick={() => saveEdit(it.id)} disabled={busy}>Save</button>
                          <button style={{ ...S.btn("ghost"), padding: "3px 8px" }} onClick={() => setEditingId(null)} disabled={busy}>Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={S.td}>{unitLabel(it)}</td>
                        <td style={S.td}>{it.min_qty}</td>
                        <td style={S.td}><strong>{money(it.unit_price, cur)}</strong></td>
                        <td style={S.td}>
                          <button style={{ ...S.btn("ghost"), padding: "3px 8px" }} aria-label="Edit price" onClick={() => startEdit(it)} disabled={busy}>Edit</button>
                          <button style={{ ...S.btn("ghost"), padding: "3px 8px" }} aria-label="Remove price" onClick={() => { if (window.confirm("Remove this price row?")) act(() => removeItem(it.id)); }} disabled={busy}>🗑</button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Add item */}
        <div style={{ ...S.card, marginTop: 10 }}>
          {!add ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div style={{ flex: 1, minWidth: 240 }}><ProductPicker products={products} onSelect={startAdd} placeholder="Add a product…" style={{ width: "100%" }} /></div>
              <span style={{ color: C.textMuted, fontSize: 12 }}>{items.length} price row{items.length === 1 ? "" : "s"}</span>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ minWidth: 180 }}><label style={S.label}>Product</label><div style={{ ...S.input, background: C.surface2 }}>{add.product.name}</div></div>
              <div style={{ width: 140 }}><label style={S.label}>Unit</label>
                <select style={S.input} value={add.unit_id} onChange={(e) => setAdd((p) => ({ ...p, unit_id: e.target.value }))} aria-label="New price unit">
                  <option value="">Base</option>
                  {unitOptions(add.product.id).map((u) => <option key={u.id} value={u.id}>{u.code}{Number(u.factor) !== 1 ? ` ×${Number(u.factor)}` : ""}</option>)}
                </select></div>
              <div style={{ width: 90 }}><label style={S.label}>Min qty</label><input style={S.input} type="number" min="1" step="1" value={add.min_qty} onChange={(e) => setAdd((p) => ({ ...p, min_qty: e.target.value }))} aria-label="New price min qty" /></div>
              <div style={{ width: 110 }}><label style={S.label}>Unit price</label><input style={S.input} type="number" min="0" step="0.01" value={add.unit_price} onChange={(e) => setAdd((p) => ({ ...p, unit_price: e.target.value }))} aria-label="New price unit price" placeholder="0.00" /></div>
              <button style={S.btn()} onClick={submitAdd} disabled={busy || add.unit_price === ""}>Add price</button>
              <button style={S.btn("ghost")} onClick={() => setAdd(null)} disabled={busy}>Cancel</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Manager (page) ─────────────────────────────────────────────────────────────
export default function PriceListManager() {
  const [lists, setLists] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);   // list | {} | null
  const [detailFor, setDetailFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setLists(await getPriceLists()); } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { getProducts({ pageSize: 1000, sortBy: "name" }).then((r) => setProducts(r.data || [])).catch(() => {}); }, []);

  const save = async (draft) => {
    if (editing && editing.id) await updatePriceList(editing.id, draft);
    else await createPriceList(draft);
    setEditing(null); await load();
  };
  const act = async (fn) => { setBusy(true); setError(""); try { await fn(); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); } };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ color: C.textMuted, fontSize: 13 }}>Named B2B price lists. Assign a list to a customer or a customer group; the resolver (WP11c) will apply it.</div>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{lists.length} list{lists.length === 1 ? "" : "s"}</span>
        <button style={S.btn()} onClick={() => setEditing({})}>+ New Price List</button>
      </div>

      <ErrorBar error={error} onDismiss={() => setError("")} />

      <div style={S.card}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Name</th><th style={S.th}>Status</th><th style={S.th}>Items</th><th style={S.th}>Currency</th><th style={S.th} aria-label="Actions"></th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={S.empty}>Loading…</td></tr>
            ) : lists.length === 0 ? (
              <tr><td colSpan={5} style={S.empty}>
                <GetStartedCTA onLoaded={load} fallback={
                  <div>
                    <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No price lists yet</div>
                    <div style={{ marginBottom: 12 }}>Create a price list, then add per-product / per-unit prices.</div>
                    <button style={S.btn()} onClick={() => setEditing({})}>+ New Price List</button>
                  </div>
                } />
              </td></tr>
            ) : lists.map((l) => (
              <tr key={l.id} style={{ cursor: "pointer" }} onClick={() => setDetailFor(l)}>
                <td style={S.td}><strong>{l.name}</strong>{l.is_default && <span style={{ ...S.pill(C.primary), marginLeft: 8 }}>default</span>}
                  {l.notes && <div style={{ color: C.textMuted, fontSize: 11, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.notes}</div>}</td>
                <td style={S.td}><span style={S.pill(STATUS_COLOR[l.status] || C.textDim)}>{l.status}</span></td>
                <td style={S.td}>{l.item_count}</td>
                <td style={S.td}>{l.currency}</td>
                <td style={S.td} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    <button style={S.btn("ghost")} title="Edit prices" onClick={() => setDetailFor(l)}>Prices</button>
                    <button style={S.btn("ghost")} title="Edit" aria-label={`Edit ${l.name}`} onClick={() => setEditing(l)}>✎</button>
                    {!l.is_default && <button style={S.btn("ghost")} title="Set as default" onClick={() => act(() => setDefaultPriceList(l.id))} disabled={busy}>Set default</button>}
                    {l.status !== "archived" && <button style={S.btn("ghost")} title="Archive" onClick={() => { if (window.confirm(`Archive "${l.name}"? It stays for history but won't be offered as active.`)) act(() => archivePriceList(l.id)); }} disabled={busy}>Archive</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && <PriceListForm initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
      {detailFor && <PriceListDetail list={detailFor} products={products} onClose={() => setDetailFor(null)} onChanged={load} />}
    </div>
  );
}
